import { api } from './api';

const NOTIFICATIONS_CHANGED_EVENT = 'carehive:notifications-changed';
const NOTIFICATION_PREFERENCES_CHANGED_EVENT = 'carehive:notification-preferences-changed';

const dispatchWindowEvent = (eventName: string) => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(eventName));
};

export const subscribeNotificationsChanged = (listener: () => void): (() => void) => {
  if (typeof window === 'undefined') return () => {};
  const wrapped = () => listener();
  window.addEventListener(NOTIFICATIONS_CHANGED_EVENT, wrapped);
  return () => window.removeEventListener(NOTIFICATIONS_CHANGED_EVENT, wrapped);
};

export const subscribeNotificationPreferencesChanged = (listener: () => void): (() => void) => {
  if (typeof window === 'undefined') return () => {};
  const wrapped = () => listener();
  window.addEventListener(NOTIFICATION_PREFERENCES_CHANGED_EVENT, wrapped);
  return () => window.removeEventListener(NOTIFICATION_PREFERENCES_CHANGED_EVENT, wrapped);
};

export interface Notification {
  id: string;
  user_id: string;
  type: 'alert' | 'task' | 'medication' | 'system' | 'communication';
  category: 'critical' | 'care' | 'admin' | 'communication';
  severity: 'low' | 'medium' | 'high' | 'critical';
  title: string;
  message: string;
  action_url?: string;
  action_label?: string;
  related_entity_type?: string;
  related_entity_id?: string;
  read: boolean;
  read_at?: string;
  clicked: boolean;
  clicked_at?: string;
  archived: boolean;
  archived_at?: string;
  metadata?: Record<string, unknown>;
  expires_at?: string;
  created_at: string;
  updated_at: string;
}

export interface NotificationFilters {
  read?: boolean;
  archived?: boolean;
  type?: string;
  category?: string;
  page?: number;
  limit?: number;
}

export interface NotificationPreferences {
  id: string;
  user_id: string;
  in_app_enabled: boolean;
  category_preferences: {
    critical: boolean;
    care: boolean;
    admin: boolean;
    communication: boolean;
  };
  quiet_hours_start?: string;
  quiet_hours_end?: string;
  quiet_hours_enabled: boolean;
  show_toast: boolean;
  updated_at: string;
}

/** Fields the user can actually change; what we PUT to the server. */
export type NotificationPreferencesUpdate = Pick<
  NotificationPreferences,
  'in_app_enabled' | 'category_preferences' | 'quiet_hours_start' | 'quiet_hours_end' | 'quiet_hours_enabled' | 'show_toast'
>;

export interface NotificationResponse {
  success: boolean;
  data: {
    notifications: Notification[];
    pagination: {
      total: number;
      page: number;
      limit: number;
      pages: number;
    };
  };
}

export interface UnreadCountResponse {
  success: boolean;
  data: {
    unreadCount: number;
  };
}

export interface PreferencesResponse {
  success: boolean;
  data: {
    preferences: NotificationPreferences;
  };
}

interface RequestOptions {
  signal?: AbortSignal;
}

/** Build axios `params` object from filters, omitting `undefined` keys. */
const buildNotificationParams = (filters: NotificationFilters): Record<string, string | number | boolean> => {
  const params: Record<string, string | number | boolean> = {};
  if (filters.read !== undefined) params.read = filters.read;
  if (filters.archived !== undefined) params.archived = filters.archived;
  if (filters.type) params.type = filters.type;
  if (filters.category) params.category = filters.category;
  if (filters.page) params.page = filters.page;
  if (filters.limit) params.limit = filters.limit;
  return params;
};

/** Get user notifications with filters and pagination. */
export const getNotifications = async (
  filters: NotificationFilters = {},
  options: RequestOptions = {}
): Promise<NotificationResponse> => {
  const response = await api.get<NotificationResponse>('/notifications', {
    params: buildNotificationParams(filters),
    signal: options.signal,
  });
  return response.data;
};

/** Get unread notification count. */
export const getUnreadCount = async (options: RequestOptions = {}): Promise<number> => {
  const response = await api.get<UnreadCountResponse>('/notifications/unread-count', {
    signal: options.signal,
  });
  return response.data.data.unreadCount;
};

/** Get single notification by ID. */
export const getNotificationById = async (id: string): Promise<Notification> => {
  const response = await api.get<{ success: boolean; data: { notification: Notification } }>(`/notifications/${id}`);
  return response.data.data.notification;
};

/** Mark notification as read. */
export const markAsRead = async (id: string): Promise<Notification> => {
  const response = await api.patch<{ success: boolean; data: { notification: Notification } }>(`/notifications/${id}/read`);
  dispatchWindowEvent(NOTIFICATIONS_CHANGED_EVENT);
  return response.data.data.notification;
};

/** Mark notification as clicked (also marks as read on the server). */
export const markAsClicked = async (id: string): Promise<Notification> => {
  const response = await api.patch<{ success: boolean; data: { notification: Notification } }>(`/notifications/${id}/click`);
  dispatchWindowEvent(NOTIFICATIONS_CHANGED_EVENT);
  return response.data.data.notification;
};

/** Mark all notifications as read. */
export const markAllAsRead = async (): Promise<number> => {
  const response = await api.patch<{ success: boolean; data: { count: number } }>('/notifications/read-all');
  dispatchWindowEvent(NOTIFICATIONS_CHANGED_EVENT);
  return response.data.data.count;
};

/** Archive notification. */
export const archiveNotification = async (id: string): Promise<Notification> => {
  const response = await api.patch<{ success: boolean; data: { notification: Notification } }>(`/notifications/${id}/archive`);
  dispatchWindowEvent(NOTIFICATIONS_CHANGED_EVENT);
  return response.data.data.notification;
};

/** Delete notification. */
export const deleteNotification = async (id: string): Promise<void> => {
  await api.delete(`/notifications/${id}`);
  dispatchWindowEvent(NOTIFICATIONS_CHANGED_EVENT);
};

/** Get user notification preferences. */
export const getPreferences = async (options: RequestOptions = {}): Promise<NotificationPreferences> => {
  const response = await api.get<PreferencesResponse>('/notifications/preferences', {
    signal: options.signal,
  });
  return response.data.data.preferences;
};

/** Update user notification preferences. Send only editable fields. */
export const updatePreferences = async (
  preferences: Partial<NotificationPreferencesUpdate>
): Promise<NotificationPreferences> => {
  const response = await api.put<PreferencesResponse>('/notifications/preferences', preferences);
  dispatchWindowEvent(NOTIFICATION_PREFERENCES_CHANGED_EVENT);
  return response.data.data.preferences;
};
